import { Skeleton } from '@hive/component';
import {
  SettingHeader,
  SettingWrapper,
} from '@hive/component/setting-components';
import { SubscriptionService } from '@hive/core/modules/cloud';
import { SubscriptionStatus } from '@hive/graphql';
import { useI18n } from '@hive/i18n';
import { track } from '@hive/track';
import { useLiveData, useService } from '@toeverything/infra';
import { useCallback, useEffect } from 'react';

import type { SettingState } from '../../types';
import { BillingHistory } from './billing-history';
import { PaymentMethod } from './payment-method';
import { ProPlanCard } from './pro-plan-card';
import * as styles from './style.css';

export const BillingSettings = ({
  onChangeSettingState,
}: {
  onChangeSettingState: (state: SettingState) => void;
}) => {
  const t = useI18n();

  return (
    <>
      <SettingHeader
        title={t['com.hive.payment.billing-setting.title']()}
        subtitle={t['com.hive.payment.billing-setting.subtitle']()}
      />
      <SettingWrapper
        title={t['com.hive.payment.billing-setting.information']()}
      >
        <SubscriptionSettings onChangeSettingState={onChangeSettingState} />
      </SettingWrapper>
      <SettingWrapper title={t['com.hive.payment.billing-setting.history']()}>
        <BillingHistory />
      </SettingWrapper>
    </>
  );
};

const SubscriptionSettings = ({
  onChangeSettingState,
}: {
  onChangeSettingState: (state: SettingState) => void;
}) => {
  const subscriptionService = useService(SubscriptionService);
  useEffect(() => {
    subscriptionService.subscription.revalidate();
    subscriptionService.prices.revalidate();
  }, [subscriptionService]);

  const teamSubscription = useLiveData(subscriptionService.subscription.pro$);

  const openPlans = useCallback(
    (scrollAnchor?: string) => {
      track.$.settingsPanel.billing.viewPlans();
      onChangeSettingState({
        activeTab: 'plans',
        scrollAnchor: scrollAnchor,
      });
    },
    [onChangeSettingState]
  );
  const gotoCloudPlansSetting = useCallback(
    () => openPlans('cloudPricingPlan'),
    [openPlans]
  );

  return (
    <div className={styles.subscription}>
      {/* Team plan card */}
      {teamSubscription !== null ? (
        <ProPlanCard gotoCloudPlansSetting={gotoCloudPlansSetting} />
      ) : (
        <SubscriptionSettingSkeleton />
      )}

      {teamSubscription !== null ? (
        teamSubscription?.status === SubscriptionStatus.Active && (
          <PaymentMethod />
        )
      ) : (
        <SubscriptionSettingSkeleton />
      )}
    </div>
  );
};

const SubscriptionSettingSkeleton = () => {
  const t = useI18n();
  return (
    <SettingWrapper title={t['com.hive.payment.billing-setting.information']()}>
      <div className={styles.subscriptionSettingSkeleton}>
        <Skeleton variant="rounded" height="104px" />
        <Skeleton variant="rounded" height="46px" />
      </div>
    </SettingWrapper>
  );
};
